/**
 * Named HTML entities → code point.
 *
 * The accented Latin-1 half is not decoration: a French publisher writes every
 * accent as a named entity, so a table limited to `&amp;`/`&lt;`/`&quot;` leaves
 * `plaque tapiss&eacute;e` on the screen for a whole recipe. Coup de Pouce emits
 * these throughout its JSON-LD instruction HTML.
 *
 * Deliberately a literal table rather than a dependency: this module is shared
 * with the React Native / web app, where `jsdom` (the only entity decoder
 * already in the tree) cannot be imported.
 */
const NAMED_ENTITIES: Record<string, number> = {
  // Markup characters
  quot: 34, amp: 38, apos: 39, lt: 60, gt: 62,
  // Latin-1 punctuation and symbols
  iexcl: 161, cent: 162, pound: 163, curren: 164, yen: 165,
  brvbar: 166, sect: 167, uml: 168, copy: 169, ordf: 170, laquo: 171,
  not: 172, reg: 174, macr: 175, deg: 176, plusmn: 177,
  sup2: 178, sup3: 179, acute: 180, micro: 181, para: 182, middot: 183,
  cedil: 184, sup1: 185, ordm: 186, raquo: 187, frac14: 188, frac12: 189,
  frac34: 190, iquest: 191, times: 215, divide: 247,
  // Latin-1 letters
  Agrave: 192, Aacute: 193, Acirc: 194, Atilde: 195, Auml: 196, Aring: 197,
  AElig: 198, Ccedil: 199, Egrave: 200, Eacute: 201, Ecirc: 202, Euml: 203,
  Igrave: 204, Iacute: 205, Icirc: 206, Iuml: 207, ETH: 208, Ntilde: 209,
  Ograve: 210, Oacute: 211, Ocirc: 212, Otilde: 213, Ouml: 214, Oslash: 216,
  Ugrave: 217, Uacute: 218, Ucirc: 219, Uuml: 220, Yacute: 221, THORN: 222,
  szlig: 223,
  agrave: 224, aacute: 225, acirc: 226, atilde: 227, auml: 228, aring: 229,
  aelig: 230, ccedil: 231, egrave: 232, eacute: 233, ecirc: 234, euml: 235,
  igrave: 236, iacute: 237, icirc: 238, iuml: 239, eth: 240, ntilde: 241,
  ograve: 242, oacute: 243, ocirc: 244, otilde: 245, ouml: 246, oslash: 248,
  ugrave: 249, uacute: 250, ucirc: 251, uuml: 252, yacute: 253, thorn: 254,
  yuml: 255,
  // Latin Extended-A / typography
  OElig: 338, oelig: 339, Scaron: 352, scaron: 353, Yuml: 376, fnof: 402,
  circ: 710, tilde: 732,
  ndash: 8211, mdash: 8212, lsquo: 8216, rsquo: 8217, sbquo: 8218,
  ldquo: 8220, rdquo: 8221, bdquo: 8222, dagger: 8224, Dagger: 8225,
  bull: 8226, hellip: 8230, permil: 8240, prime: 8242, Prime: 8243,
  lsaquo: 8249, rsaquo: 8250, oline: 8254, frasl: 8260, euro: 8364,
  trade: 8482, larr: 8592, uarr: 8593, rarr: 8594, darr: 8595, harr: 8596,
  minus: 8722,
};

/**
 * Decode HTML entities in a plain string (no tag stripping).
 * Handles numeric (&#8217;, &#x2019;) and named entities.
 * Used for JSON-LD fields like title/description where some sites incorrectly
 * embed HTML entities inside JSON strings.
 *
 * An unrecognised entity is left verbatim — mangling `&foo;` into something
 * else would be worse than showing it.
 */
export function decodeHtmlEntities(text: string): string {
  if (!text || !/&/.test(text)) return text;
  return text
    .replace(/&#x([0-9a-f]+);/gi, (_, n) => String.fromCodePoint(parseInt(n, 16)))
    .replace(/&#(\d+);/g, (_, n) => String.fromCodePoint(parseInt(n, 10)))
    // Space-like entities become an ordinary space, and zero-width ones vanish,
    // rather than decoding to their literal code points. An ingredient line is
    // not just displayed — it is parsed, normalized and matched against the
    // pantry, and a U+00A0 or an invisible U+00AD sitting inside "1½ tasse de
    // farine" defeats that silently, with nothing wrong on screen to explain it.
    .replace(/&(nbsp|ensp|emsp|thinsp);/g, ' ')
    .replace(/&(shy|zwnj|zwj|lrm|rlm);/g, '')
    .replace(/&([a-z][a-z0-9]*);/gi, (match, name: string) => {
      const code = NAMED_ENTITIES[name];
      return code === undefined ? match : String.fromCodePoint(code);
    });
}

/**
 * Strip inline HTML from recipe instruction text.
 * Some recipe sources (e.g. Ricardo Cuisine) embed <a> affiliate links
 * inside JSON-LD HowToStep.text — this removes the markup while keeping
 * the visible label text.
 */
export function stripInstructionHtml(text: any): string {
  if (!text) return '';
  const str = typeof text === 'string' ? text : String((text as any)?.step ?? text);
  if (!/<[a-z]/i.test(str) && !/&[a-z#]/i.test(str)) return str;

  return decodeHtmlEntities(
    str
      .replace(/<a\b[^>]*>(.*?)<\/a>/gi, '$1')
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p>/gi, '\n\n')
      .replace(/<\/div>/gi, '\n')
      .replace(/<[^>]+>/g, '')
  )
    .replace(/\n\s*\n\s*\n/g, '\n\n')
    .trim();
}

/**
 * A description as plain text, however many times the publisher escaped it.
 *
 * Descriptions arrive as HTML (Zeste: `<p>…l&eacute;g&egrave;rement…</p>`) and
 * sometimes as HTML escaped again (K pour Katrine: `&lt;p class=&quot;Corps&quot;&gt;`).
 * Stripping tags before decoding entities, once, put the second kind on screen
 * as literal `<p class="Corps">` markup, and a table limited to `&amp;`/`&lt;`
 * left the first kind's accents as `&eacute;`. This strips and decodes until
 * nothing changes (three rounds at most), keeping paragraphs as blank lines
 * and collapsing the runs of spaces the markup leaves behind.
 */
export function htmlToPlainText(text: string | null | undefined): string {
  if (!text) return '';
  let current = text;
  for (let round = 0; round < 3; round++) {
    if (!/<\/?[a-z][^>]*>/i.test(current) && !/&(?:[a-z][a-z0-9]*|#x?[0-9a-f]+);/i.test(current)) break;
    const next = decodeHtmlEntities(
      current
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/(?:p|div|li|h[1-6])>/gi, '\n\n')
        .replace(/<\/?[a-z][^>]*>/gi, ''),
    );
    if (next === current) break;
    current = next;
  }
  return current
    .split('\n')
    .map(line => line.replace(/[ \t ]+/g, ' ').trim())
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}
